import { useState } from 'react';
import { useApi } from '../../api/hooks.js';
import { getAdminScore } from '../api.js';
import { ChartNote, LineChart, StackedBar } from '../charts.js';
import { isUuid } from '../forms.js';
import { fmtWhen, humanise } from '../labels.js';
import { adminHash } from '../routes.js';
import { Card, Empty, Head, IdChip, Loading, show, Trouble } from '../ui.js';

/**
 * One person's protection score, its components, and the journal of why it
 * moved. Read-only by design: the score has exactly one writer, and it is not
 * this console. The ID is in the hash (#/scores/<id>) so a row on the review
 * screen can link straight here and the lookup survives a reload.
 */

export function ScoresScreen({ userRef }: { userRef: string | null }) {
  const [input, setInput] = useState(userRef ?? '');
  const valid = userRef !== null && isUuid(userRef);
  const score = useApi(() => getAdminScore(userRef ?? ''), [userRef], { skip: !valid });

  const noScore = score.error !== null && score.error.code === 'SCORE_NOT_FOUND';

  return (
    <>
      <Head
        title="Score lookup"
        lede="A person's protection score, what makes it up, and why it has moved. Read-only — scores are calculated by the system and cannot be edited."
      />

      <Card title="Find a person">
        <form
          className="cr-strip"
          onSubmit={(event) => {
            event.preventDefault();
            const trimmed = input.trim();
            if (trimmed !== '') window.location.hash = adminHash('scores', trimmed);
          }}
        >
          <span className="cr-strip-label">Person ID</span>
          <input
            type="text"
            className="cr-mono"
            placeholder="Paste the person's ID"
            value={input}
            onChange={(event) => setInput(event.target.value)}
            aria-label="Person ID"
          />
          <button type="submit" className="cr-btn primary" disabled={input.trim() === ''}>
            Look up
          </button>
        </form>
        <p className="cr-fine" style={{ margin: '4px 0 0' }}>
          People are identified by ID only — never by name or phone number. IDs appear on the review screen
          and can be copied from there.
        </p>
        {userRef !== null && !valid && (
          <ul className="cr-problems" role="alert">
            <li>That is not a person ID. IDs look like 6f3f1a2e-1111-4222-8333-444455556666.</li>
          </ul>
        )}
      </Card>

      {valid && (
        <>
          {noScore ? (
            <Card title="Score">
              <Empty>
                No score yet for <IdChip value={userRef} what="person ID" />. This is normal before the person
                has enrolled or been scanned for the first time.
              </Empty>
            </Card>
          ) : score.error !== null ? (
            <Trouble error={score.error} />
          ) : score.data === null ? (
            <Card title="Score">
              <Loading />
            </Card>
          ) : (
            <>
              <Card
                title="Score"
                aside={
                  <span className="cr-fine">
                    Calculated {fmtWhen(score.data.score.computed_at)} · <IdChip value={userRef} what="person ID" />
                  </span>
                }
              >
                <div className="cr-figure-row">
                  <span className="cr-figure">{String(score.data.score.score)}</span>
                  <div style={{ flex: 1, minWidth: 260 }}>
                    <StackedBar
                      ariaLabel="How the score is made up"
                      format={(value) => String(value)}
                      segments={Object.entries(score.data.score.components ?? {}).map(([name, value]) => ({
                        key: name,
                        label: humanise(name),
                        value: typeof value === 'number' ? value : 0,
                      }))}
                    />
                  </div>
                </div>
              </Card>

              <Card title="Score over time">
                {score.data.events.length === 0 ? (
                  <Empty>No history yet.</Empty>
                ) : (
                  <>
                    <LineChart
                      ariaLabel="Protection score over time"
                      format={(value) => String(value)}
                      formatWhen={(ms) => fmtWhen(new Date(ms).toISOString())}
                      points={score.data.events
                        .map((entry) => ({
                          at: new Date(entry.created_at).getTime(),
                          value: entry.score_after,
                          note: `${humanise(entry.cause_kind)} · ${entry.delta > 0 ? '+' : ''}${String(entry.delta)} ${humanise(entry.component).toLowerCase()}`,
                        }))
                        .filter((point) => Number.isFinite(point.at) && Number.isFinite(point.value))}
                    />
                    <ChartNote>Each point is a recorded change. Hover, or use the arrow keys, to see the reason.</ChartNote>
                  </>
                )}
              </Card>

              <Card title="Why it moved" aside={<span className="cr-fine">Newest first</span>}>
                {score.data.events.length === 0 ? (
                  <Empty>No changes recorded yet.</Empty>
                ) : (
                  <div className="cr-table-wrap">
                    <table>
                      <thead>
                        <tr>
                          <th>When</th>
                          <th className="num">Change</th>
                          <th>Component</th>
                          <th>Reason</th>
                          <th className="num">Score after</th>
                        </tr>
                      </thead>
                      <tbody>
                        {score.data.events.map((entry, index) => (
                          <tr key={`${String(entry.score_event_id ?? index)}-${entry.created_at}`}>
                            <td>{fmtWhen(entry.created_at)}</td>
                            <td className={`num cr-delta ${entry.delta > 0 ? 'up' : entry.delta < 0 ? 'down' : ''}`}>
                              {entry.delta > 0 ? `+${String(entry.delta)}` : String(entry.delta)}
                            </td>
                            <td>{humanise(entry.component)}</td>
                            <td>{humanise(entry.cause_kind)}</td>
                            <td className="num">{show(entry.score_after)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </Card>
            </>
          )}
        </>
      )}
    </>
  );
}
